import {
  buildPayloadFromQuery,
  getInitialPropertyTypes,
} from 'components/marketplace/helpers/useSearchState';
import type { GeoPoint } from 'components/marketplace/map/types';
import type { MarketplaceProperty } from 'components/marketplace/property/types';
import type { InferGetServerSidePropsType } from 'declarations/shared/serverSideProps';
import type { getServerSideProps } from 'pages/homes/[market]';

const PROPERTY_SEARCH_RADIUS_DEGREES = 0.04;

export const propertyMapSearch = (
  property: MarketplaceProperty,
): InferGetServerSidePropsType<typeof getServerSideProps> => {
  const { latitude, longitude } = property;
  const hasCoordinates = latitude != null && longitude != null;
  const south = hasCoordinates ? latitude - PROPERTY_SEARCH_RADIUS_DEGREES : 24;
  const north = hasCoordinates ? latitude + PROPERTY_SEARCH_RADIUS_DEGREES : 50;
  const west = hasCoordinates ? longitude - PROPERTY_SEARCH_RADIUS_DEGREES : -125;
  const east = hasCoordinates ? longitude + PROPERTY_SEARCH_RADIUS_DEGREES : -66;
  const vertices: GeoPoint[] = [
    { lat: south, lon: west },
    { lat: north, lon: west },
    { lat: north, lon: east },
    { lat: south, lon: east },
    { lat: south, lon: west },
  ];
  const queryParams = { vertices, preview_aid: property.addressToken };
  const areaName = hasCoordinates
    ? [property.city, property.state].filter(Boolean).join(', ')
    : 'Homes';
  return {
    // Browse data can load independently; a search outage must not block the home itself.
    dehydratedState: { mutations: [], queries: [] },
    initialVariables: buildPayloadFromQuery(
      { ...queryParams, ...getInitialPropertyTypes(queryParams) },
      { market: null, vertices, zoneIds: null },
    ),
    initialSearchPathname: '/homes',
    marketInfo: {
      displayName: areaName,
      name: areaName,
      identifier: null,
      shape: { type: 'Polygon', coordinates: [vertices] },
    },
    queryParams,
    activeMarkets: [],
    canonicalOverride: '/homes',
    savedSearchId: null,
    initialZoneIds: null,
    initialViewportAreaName: areaName,
    ssrListingCount: null,
    stagedPhotosExposure: null,
    mapRefreshEnabled: true,
  };
};
